// utils/swal.js
// Wrapper temático de SweetAlert2 con la identidad Quantus Invoice
// (Sirius Solutions). Centraliza estilos y comportamiento para que la
// adopción en la plataforma sea consistente y de bajo riesgo.
//
// IMPORTANTE: NO reemplaza react-toastify (164 usos). Se añade para los
// momentos de alto valor: confirmaciones destructivas, feedback de auth
// y diálogos críticos donde un modal de marca aporta más que un toast.

import Swal from "sweetalert2";

// Paleta Quantus (coherente con tailwind.config.js)
const Q = {
  blue: "#3039AC",
  royal: "#3D5AF1",
  teal: "#23C0D6",
  navy: "#141B34",
  ink: "#1A1A1A",
  danger: "#dc2626",
};

// Instancia con defaults de marca (botones, tipografía, foco).
const QSwal = Swal.mixin({
  confirmButtonColor: Q.blue,
  cancelButtonColor: "#64748b",
  buttonsStyling: true,
  reverseButtons: true,
  focusConfirm: false,
  customClass: {
    popup: "q-swal-popup",
    title: "q-swal-title",
    confirmButton: "q-swal-confirm",
    cancelButton: "q-swal-cancel",
  },
});

// Inyecta una vez los estilos de marca (Kanit + bordes redondeados).
function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("q-swal-styles")) return;
  const css = `
    .q-swal-popup{border-radius:1rem;font-family:'Inter',sans-serif;}
    .q-swal-title{font-family:'Kanit','Inter',sans-serif;font-weight:600;color:#1d2740;}
    .q-swal-confirm{font-family:'Kanit','Inter',sans-serif;font-weight:600;border-radius:.5rem;padding:.6rem 1.4rem;}
    .q-swal-cancel{font-family:'Kanit','Inter',sans-serif;font-weight:600;border-radius:.5rem;padding:.6rem 1.4rem;}
    .swal2-timer-progress-bar{background:${Q.teal};}
  `;
  const tag = document.createElement("style");
  tag.id = "q-swal-styles";
  tag.appendChild(document.createTextNode(css));
  document.head.appendChild(tag);
}
ensureStyles();

/** Modal de éxito. */
export const swalSuccess = (title, text = "") =>
  QSwal.fire({ icon: "success", title, text, iconColor: Q.teal });

/** Modal de error. */
export const swalError = (title = "Ocurrió un error", text = "") =>
  QSwal.fire({ icon: "error", title, text, iconColor: Q.danger });

/** Modal informativo. */
export const swalInfo = (title, text = "") =>
  QSwal.fire({ icon: "info", title, text, iconColor: Q.royal });

/**
 * Confirmación. Devuelve Promise<boolean> (true si el usuario confirma).
 * Reemplazo de marca para window.confirm() — mismo flujo booleano.
 */
export const swalConfirm = async (
  title,
  text = "",
  { confirmText = "Confirmar", cancelText = "Cancelar", danger = false } = {}
) => {
  const res = await QSwal.fire({
    icon: danger ? "warning" : "question",
    title,
    text,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    confirmButtonColor: danger ? Q.danger : Q.blue,
    iconColor: danger ? Q.danger : Q.royal,
  });
  return res.isConfirmed === true;
};

/** Toast no bloqueante (esquina superior derecha, auto-cierre). */
export const swalToast = (title, icon = "success", timer = 2600) => {
  ensureStyles();
  return Swal.fire({
    toast: true,
    position: "top-end",
    icon,
    title,
    showConfirmButton: false,
    timer,
    timerProgressBar: true,
    iconColor: icon === "success" ? Q.teal : icon === "error" ? Q.danger : Q.royal,
    customClass: { popup: "q-swal-popup", title: "q-swal-title" },
  });
};

export default QSwal;
